import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Sparkles } from 'lucide-react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

import { ERROR_CODES, parseStructuredError } from '@modules/error/errorCodes'
import { useImageDownload } from '@modules/hooks/useImageDownload'
import { useHandleError } from '@modules/hooks/useSending'
import { Card } from '@modules/shadcn/ui/Card/Card'
import { formatTimestamp } from '@modules/utils/dateUtils'

import {
  type ProcessImageParameters,
  type ProcessImageRequest,
  ProcessImageRequestSchema,
  ProcessImageType,
} from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import { MaskType } from '@ai-color-modules/gen_proto/ai_color_simulation/v1/process_image_pb'
import {
  type CheckMaskImagesReadyResponse,
  type GetDealWithColorSimulationResponse,
  type OriginalColorSimulationImages,
} from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'
import { getPermissions, type SimulationRole } from '@ai-color-modules/permissions'
import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import { useCanvasToolMode } from '@ai-color-modules/simulation/hooks/useCanvasToolMode'
import { useExcludeAreaSelection } from '@ai-color-modules/simulation/hooks/useExcludeAreaSelection'
import { useImageMetrics } from '@ai-color-modules/simulation/hooks/useImageMetrics'
import { useSubAreaSelection } from '@ai-color-modules/simulation/hooks/useSubAreaSelection'
import type {
  MaterialOption,
  MaterialTargetPart,
} from '@ai-color-modules/simulation/MaterialPalette/materialOptions'
import { SimulationEditorCard } from '@ai-color-modules/simulation/SimulationEditorCard/SimulationEditorCard'
import { SimulationUploadCard } from '@ai-color-modules/simulation/SimulationUploadCard/SimulationUploadCard'
import { filterVisibleSimulatedImages } from '@ai-color-modules/utils/imageFilters'
import { getMaskUrls } from '@ai-color-modules/utils/maskUtils'

import { AreaColorSelector } from './AreaColorSelector'
import { AreaOverlay } from './AreaOverlay'
import { AiImageCard } from './components/AiImageCard'
import { ColorSelectDialog } from './components/ColorSelectDialog'
import { CustomAreaSettingDialog } from './components/CustomAreaSettingDialog'
import { CustomerHistoryList, CustomerPinnedStrip } from './components/CustomerHistoryList'
import { CustomerTitleSheet } from './components/CustomerTitleSheet'
import { FloatingToolbar } from './components/FloatingToolbar'
import { HistoryFilterChips } from './components/HistoryFilterChips'
import { MaskColorOverlay } from './components/MaskColorOverlay'
import { PinnedHistoryPanel } from './components/PinnedHistoryPanel'
import { PinReplaceDialog } from './components/PinReplaceDialog'
import { PolygonMaskOverlay } from './components/PolygonMaskOverlay'
import { ShareDialog, type ShareFallbackReason } from './components/ShareDialog'
import { SharedAnchorBanner, SharedAnchorReturnPill } from './components/SharedAnchorBanner'
import { SimulationControlBar } from './components/SimulationControlBar'
import { SimulationImagePanel } from './components/SimulationImagePanel'
import { VertexContextMenu } from './components/VertexContextMenu'
import { ZoomableImage } from './components/ZoomableImage'
import { ZoomControls } from './components/ZoomControls'
import {
  AI_COLOR_MAX_ZOOM_SCALE,
  AI_COLOR_MIN_ZOOM_SCALE,
  AI_COLOR_PREVIEW_MIN_ZOOM_SCALE,
  AI_COLOR_RESULT_DISCLAIMER,
  AI_COLOR_ZOOM_STEP,
} from './constants'
import { useFormChangeDetection, useFormParametersSync } from './hooks/useFormParametersSync'
import { useLayerEditingStateSync } from './hooks/useLayerEditingStateSync'
import { useMaskPolling } from './hooks/useMaskPolling'
import { useProgressiveImageSrc } from './hooks/useProgressiveImageSrc'
import { useSimulationDetailState } from './hooks/useSimulationDetailState'
import { useSummaryImageDownload } from './hooks/useSummaryImageDownload'
import { useHistoryFilter } from './hooks/useHistoryFilter'
import { type ThumbnailItem, useThumbnailGroups } from './hooks/useThumbnailGroups'
import { AI_COLOR_PIN_LIMIT, formatHistoryDate, resolveHistoryTitle } from './utils/historyDisplay'
import { SimulationDetailHeader } from './SimulationDetailHeader'
import {
  buildColorOptionsFromParameters,
  mergeColorOptionsWithFallback,
} from './utils/colorOptionFallback'
import {
  ALL_COLORS_SENTINEL,
  type AreaColorPreset,
  buildAreaPresetSelection,
  editingAreaToAreaKey,
  resolveDialogColors,
  resolveEffectivePresetId,
} from './utils/presetSelection'
import {
  BASE_AREAS,
  buildProcessImageParameters,
  hasAnyMergedPolygons,
} from './utils/processParameters'
import { getProcessedImageSelectionState } from './utils/selectionState'

// ── API prop 型定義（ロール別の discriminated union） ──

/** processImage mutation の型 */
export interface ProcessImageMutationProp {
  mutateAsync: (request: ProcessImageRequest) => Promise<unknown>
  isPending: boolean
}

/** dealName mutation の型 */
export interface DealNameMutationProp {
  mutateAsync: (dealName: string) => Promise<unknown>
  isPending: boolean
}

/** 共通 API props（全ロール共有） */
interface BaseProps {
  data: GetDealWithColorSimulationResponse
  /** カラーシミュレーション実行 mutation */
  processImageMutation?: ProcessImageMutationProp
  /** 画像のお気に入りを更新する関数 */
  onToggleImageFavorite?: (imageId: number, favorited: boolean) => Promise<void> | void
  /** 履歴のタイトルを更新する関数（企業・施主の両方。issue #2370）。空文字列は未設定に戻す */
  onUpdateImageTitle?: (imageId: number, title: string) => Promise<void> | void
  /** API ( ListCompanyColors / ListSharedCompanyColors ) から取得した色マスター */
  availableColors: ColorOption[]
  /** マスク画像生成完了をチェックする関数（ポーリング用） */
  checkMaskReady?: (imageId: bigint) => Promise<CheckMaskImagesReadyResponse>
  /** マスクURLのみを取得する関数（ポーリング完了時に呼ばれる、dataを更新せずマスクURLだけ取得） */
  fetchMaskUrls?: (imageId: bigint) => Promise<Record<string, string>>
  /** API ( ListCompanyMaterials ) から取得した素材マスター。屋根・外壁ベースエリアの素材変更に使用 */
  availableMaterials?: MaterialOption[]
}

/** Company 向け API props */
interface CompanyProps extends BaseProps {
  role?: 'company'
  s3Upload: (file: File) => Promise<void>
  uploading: boolean
  dealNameMutation: DealNameMutationProp
  /** 現地調査報告書のURL（新規タブで開く） */
  surveyReportUrl?: string
  /**
   * 現地調査報告書ボタンを押したときの処理。`surveyReportUrl` が無い（まだ飛び先の案件が
   * 決まっていない）ときに、同じ位置のボタンから案件を選ぶ・作るダイアログを開くのに使う。
   */
  onSurveyReportClick?: () => void
  /** ベースエリアのマスクを再生成する関数 */
  regenerateMask?: (imageUrl: string, maskType: MaskType, baseImageId: number) => Promise<void>
  /** ベースエリアのマスクを再生成中かどうか */
  isMaskRegenerating?: boolean
  /**
   * 共有リンクを生成する関数。戻り値は URL（と、あればサーバーが返す有効期限）。
   * `anchorImageId` は施主が着地する履歴（issue #2950）。未指定ならプロジェクト単位のリンク。
   */
  createShareLink?: (params: {
    baseImageId: number
    parameters?: ProcessImageParameters
    /** エリア別プリセット選択（施主拘束用）. base = canonical英語名キー, custom = index文字列キー. */
    areaPresetSelection?: {
      baseAreas: Record<string, string>
      customAreas: Record<string, string>
    }
    anchorImageId?: number
  }) => Promise<string | { url: string; expiresAt?: Date }>
  /**
   * 履歴のピン留めを設定・解除する関数（企業のみ。issue #2953）。
   * `replaceImageId` は上限（4件）のときに代わりに外す履歴。
   */
  onUpdateImagePin?: (
    imageId: number,
    pinned: boolean,
    replaceImageId?: number,
  ) => Promise<void> | void
  /**
   * 企業の名前付きプリセット一覧（色は display_order 順で解決済み）.
   * 色指定ダイアログのプリセット切替に使う.
   *
   * `undefined` = **未ロード**（API 解決前）／`[]` = プリセットが 1 件も無い企業（正当な状態）.
   * この 2 状態は共有リンク作成のガードで区別するため、呼び出し側は未ロードを `[]` に
   * 潰さずそのまま `undefined` を渡すこと.
   */
  colorPresets?: AreaColorPreset[]
  /**
   * ベースエリアごとのデフォルトプリセット ID（canonical 英語名キー）.
   * ベースエリアの色指定ダイアログの初期選択に使う.
   *
   * `undefined` = **未ロード**（API 解決前）／`{}` = ロード済みでデフォルト未設定（正当な状態.
   * `GetBaseAreaPresetDefaults` は未設定エリアをレスポンスに含めない契約なので空は正常）.
   * `colorPresets` と同じく共有リンク作成のガードで 2 状態を区別するため、呼び出し側は
   * 未ロードを `{}` に潰さずそのまま `undefined` を渡すこと.
   */
  baseAreaDefaultPresetIds?: Record<string, string>
  /** シミュレーション結果画像を削除する関数 */
  onDeleteSimulatedImage?: (imageId: number) => Promise<void> | void
  /** 変換情報付きDL の右上に描画する会社名 */
  companyName?: string
  /** 変換情報付きDL の右上に描画する会社ロゴ URL（未設定／読込失敗時は会社名のみ） */
  companyLogoUrl?: string
  /**
   * 「新しい外観カットを作成」カードの描画をホストに委ねる（企業画面のプラン別 UI 用）。
   *
   * カードそのものはここで組み立て、包み方（無効表現・消費クレジットの表記）だけを
   * ホストに任せる。未指定ならカードをそのまま描画する（従来挙動）。
   */
  renderUploadCard?: (card: React.ReactNode) => React.ReactNode
  /** 外観カットのアップロード失敗時にエラー文の下へ添える一文（企業画面のクレジットの案内）。 */
  uploadErrorNote?: string
}

/** Customer 向け API props */
interface CustomerProps extends BaseProps {
  role: 'customer'
  /**
   * カット（外観カット=元画像）単位の編集可能ベースエリア。customer ロールのみ。
   * キー = 元画像 ID の文字列、値 = 日本語ベースエリア名（BASE_AREAS の順）。
   *
   * - prop 自体が未指定（company ロール / 未ロード）なら BASE_AREAS 全件にフォールバックする
   * - prop があってカットのエントリが無い / 空配列なら「そのカットは編集不可」として空表示にする
   *   （企業が色変更していないカットを施主が編集できてはいけないため、全件フォールバックしない）
   */
  visibleBaseAreasByImageId?: Record<string, string[]>
  /**
   * 施主側でエリアごとに表示可能な色一覧（area_key ベース）.
   * area_key: ベース = canonical英語名（"outer wall and balcony" 等）,
   * カスタム = "<base_image_id>:<index>"（例 "1:0"）.
   * サーバがプリセット + 履歴色を解決済み。企業指定プリセットへの拘束を表現する.
   */
  availableColorsByArea?: Record<string, ColorOption[]>
  /**
   * 共有リンクのアンカー（施主が着地する履歴。issue #2950）。
   * `imageId` の履歴を最初に開き、帯「〇〇から共有された履歴」と戻りピルを出す。
   * 一覧に無ければ（削除済み等）従来どおり最新の履歴を開き、帯もピルも出さない。
   */
  sharedAnchor?: { imageId: bigint; companyName: string }
}

export type AiColorSimulationDetailProps = CompanyProps | CustomerProps

/**
 * 内部用: discriminated union → フラットな optional props に変換する型.
 * コンポーネント実装側では company-only props を optional として安全に参照する.
 */
type ResolvedProps = BaseProps & { role?: SimulationRole } & Partial<
    Omit<CompanyProps, keyof BaseProps | 'role'>
  > &
  Partial<Omit<CustomerProps, keyof BaseProps | 'role'>>

const AREA_MASK_TYPE_MAP: Record<string, MaskType> = {
  外壁: MaskType.WALL,
  屋根: MaskType.ROOF,
  雨樋: MaskType.GUTTER,
  '破風・鼻隠し': MaskType.FASCIA,
}

// 後方互換: ベースエリア wall/roof/gutter の3種のマスクがすべて揃っているかチェック
// （fasciaは含めない。is_ready判定と整合させ、既存案件でも操作可能とする）
const hasAllMaskImages = (image: OriginalColorSimulationImages | undefined): boolean => {
  if (!image?.simulatedImage) return false
  const existingTypes = new Set(image.simulatedImage.map(simImg => simImg.type))
  return (
    existingTypes.has(ProcessImageType.MASKED_WALL) &&
    existingTypes.has(ProcessImageType.MASKED_ROOF) &&
    existingTypes.has(ProcessImageType.MASKED_GUTTER)
  )
}

type PointArray = { originalX: number; originalY: number }[]

/**
 * 施主側でMaskColorOverlayに渡すポリゴン一覧を構築する。
 * カスタムエリアと除外エリアの両方を含む。
 */
export const buildExcludePolygonsForCustomer = (
  role: SimulationRole | undefined,
  subAreas: { points: PointArray; polygons?: PointArray[] }[],
  excludeAreas: { points: PointArray }[],
): PointArray[] | undefined => {
  if (role !== 'customer') return undefined
  const polygons: PointArray[] = []
  for (const area of subAreas) {
    if (area.points.length >= 3) {
      polygons.push(area.points)
    }
    if (area.polygons) {
      for (const poly of area.polygons) {
        if (poly.length >= 3) {
          polygons.push(poly)
        }
      }
    }
  }
  for (const area of excludeAreas) {
    if (area.points.length >= 3) {
      polygons.push(area.points)
    }
  }
  return polygons.length > 0 ? polygons : undefined
}

export const SimulationDetail: React.FC<AiColorSimulationDetailProps> = _props => {
  const {
    data,
    role = 'company',
    s3Upload,
    uploading = false,
    processImageMutation,
    dealNameMutation,
    surveyReportUrl,
    onSurveyReportClick,
    checkMaskReady,
    fetchMaskUrls,
    regenerateMask,
    isMaskRegenerating = false,
    createShareLink,
    onToggleImageFavorite,
    onUpdateImageTitle,
    onUpdateImagePin,
    onDeleteSimulatedImage,
    sharedAnchor,
    availableColors,
    availableMaterials,
    visibleBaseAreasByImageId,
    availableColorsByArea,
    colorPresets,
    baseAreaDefaultPresetIds,
    companyName,
    companyLogoUrl,
    renderUploadCard,
    uploadErrorNote,
  } = _props as ResolvedProps
  const permissions = getPermissions(role)
  const { handleError } = useHandleError()
  const processing = processImageMutation?.isPending ?? false
  const images = useMemo(() => data.images ?? [], [data.images])

  const tabs = useMemo(() => {
    return images.map((image, index) => ({
      value: image.id ? image.id.toString() : `image-${index}`,
      label: `外観カット${index + 1}`,
      index,
    }))
  }, [images])

  const simulationState = useSimulationDetailState({
    images,
    tabs,
    initialSelectedImageId: sharedAnchor?.imageId,
  })

  // 画像コンテナのref
  const originalImageContainerRef = useRef<HTMLDivElement>(null)
  const processedImageContainerRef = useRef<HTMLDivElement>(null)
  // ズーム制御用ref
  const originalZoomRef = useRef<ReactZoomPanPinchRef>(null)
  const processedZoomRef = useRef<ReactZoomPanPinchRef>(null)
  const {
    activeTab,
    editingArea,
    setEditingArea,
    openLayerId,
    setOpenLayerId,
    areaColors,
    setAreaColors,
    areaMaterials,
    setAreaMaterials,
    mergedPolygonsByArea,
    setMergedPolygonsByArea,
    areaSelectionMode,
    setAreaSelectionMode,
    userSelectedPresetByArea,
    setUserSelectedPresetByArea,
    isSubAreaModeActive,
    setIsSubAreaModeActive,
    excludeEnabled,
    setExcludeEnabled,
    isVerticalLayout,
    setIsVerticalLayout,
    isOriginalHidden,
    setIsOriginalHidden,
    setDisplayMode,
    isEditMode,
    isPreviewMode,
    unifyColor,
    setUnifyColor,
    isCustomAreaSettingOpen,
    setIsCustomAreaSettingOpen,
    editingCustomAreaIndex,
    setEditingCustomAreaIndex,
    isShareDialogOpen,
    setIsShareDialogOpen,
    shareUrl,
    setShareUrl,
    isShareLinkLoading,
    setIsShareLinkLoading,
    shareError,
    setShareError,
    maskAdjustError,
    setMaskAdjustError,
    selectedProcessedIndexByTab,
    originalDimensions,
    currentTabImages,
    showOriginalImage,
    handleImageLoad,
    handleOpenColorPalette,
    handleDismissColorPalette,
    handleCloseColorPalettePanel,
    handleBaseAreaReset,
    handleThumbnailSelect,
  } = simulationState

  // 画像メトリクスのhook
  const { getMetrics: getOriginalMetrics, containerSize: originalContainerSize } = useImageMetrics({
    containerRef: originalImageContainerRef as React.RefObject<HTMLElement>,
    originalDimensions,
    dependencies: [
      activeTab,
      isSubAreaModeActive,
      excludeEnabled,
      isVerticalLayout,
      showOriginalImage,
    ],
  })

  const { getMetrics: getProcessedMetrics, containerSize: processedContainerSize } =
    useImageMetrics({
      containerRef: processedImageContainerRef as React.RefObject<HTMLElement>,
      originalDimensions,
      dependencies: [
        activeTab,
        excludeEnabled,
        isSubAreaModeActive,
        isVerticalLayout,
        showOriginalImage,
      ],
    })

  const getSubAreaMetrics = useCallback(
    () => (showOriginalImage ? getOriginalMetrics() : getProcessedMetrics()),
    [showOriginalImage, getOriginalMetrics, getProcessedMetrics],
  )

  // サブエリア選択のhook
  const subAreaSelection = useSubAreaSelection({
    getMetrics: getSubAreaMetrics,
    originalDimensions,
  })

  // 除外エリア選択のhook
  const excludeAreaSelection = useExcludeAreaSelection({
    getMetrics: getProcessedMetrics,
    originalDimensions,
    excludeEnabled,
    setExcludeEnabled,
  })

  // parametersから復元した初期状態を保持し、フォーム変更を検知
  const { initialFormState } = useFormParametersSync({
    images,
    activeTab,
    selectedProcessedIndexByTab,
    setAreaColors,
    setAreaMaterials,
    setMergedPolygonsByArea,
    setAreaSelectionMode,
    setUnifyColor,
    setSubAreas: subAreaSelection.setSubAreas,
    setExcludeAreas: excludeAreaSelection.setExcludeAreas,
    setIsSubAreaModeActive,
    setExcludeEnabled,
  })

  // フォームが初期状態から変更されたかどうかをチェック
  const hasFormChanged = useFormChangeDetection(
    initialFormState,
    areaColors,
    subAreaSelection.subAreas,
    excludeAreaSelection.excludeAreas,
    unifyColor,
    areaMaterials,
    areaSelectionMode,
    mergedPolygonsByArea,
  )

  // 現在選択中の外観カットの情報を取得
  const currentTab = tabs.find(tab => tab.value === activeTab)
  const currentImage: OriginalColorSimulationImages | undefined = currentTab
    ? images[currentTab.index]
    : undefined
  const currentProcessedImages = filterVisibleSimulatedImages(currentImage?.simulatedImage)
  const { selectedProcessedIndex } = getProcessedImageSelectionState(
    activeTab,
    currentProcessedImages.length,
    selectedProcessedIndexByTab,
  )
  const selectedProcessedImage =
    typeof selectedProcessedIndex === 'number'
      ? currentProcessedImages[selectedProcessedIndex]
      : undefined
  // 閲覧モードの結果パネルは軽いプレビューを先に出し、原寸を背景で読み込んで差し替える（issue #2466）。
  // 編集モードはオーバーレイの座標計算が原寸の naturalWidth/Height に依存するため最初から原寸。
  const processedImageSrc = useProgressiveImageSrc({
    imageKey: selectedProcessedImage?.id?.toString(),
    previewUrl: selectedProcessedImage?.previewUrl,
    url: selectedProcessedImage?.url,
    enabled: !isEditMode,
  })

  // アサイン表示（レイヤーのスウォッチ + コード）用の色一覧。
  // 履歴色（プリセット外の過去使用色）はカラーカタログ（availableColors）に含まれなく
  // なったため、選択中の結果画像 Parameters から code→ColorOption を合成し、
  // 「カタログ lookup → 画像 Parameters フォールバック」の順で解決できるようにする。
  // company / customer どちらも AreaColorSelector を経由するのでここで一括対応できる。
  const displayColorOptions = useMemo(
    () =>
      mergeColorOptionsWithFallback(
        availableColors ?? [],
        buildColorOptionsFromParameters(selectedProcessedImage?.parameters),
      ),
    [availableColors, selectedProcessedImage?.parameters],
  )

  // 施主側: 現在のカット（外観カット）で編集可能なベースエリア。
  // タブ切替に追従させるため、カット単位のマップから currentImage.id で引く。
  // prop 自体が undefined（company / 未ロード）のときだけ全件フォールバック（下の ?? BASE_AREAS）に委ねる。
  const visibleBaseAreas = useMemo(() => {
    if (!visibleBaseAreasByImageId) return undefined
    return visibleBaseAreasByImageId[currentImage?.id?.toString() ?? ''] ?? []
  }, [visibleBaseAreasByImageId, currentImage?.id])

  // プレビュー表示では縮小操作を提供しない（issue #2417）。
  // ホイール・ピンチの下限を等倍に上げ、縮小ボタン自体も出さない。
  const canZoomOutCanvas = !isPreviewMode
  const canvasMinZoomScale = isPreviewMode
    ? AI_COLOR_PREVIEW_MIN_ZOOM_SCALE
    : AI_COLOR_MIN_ZOOM_SCALE

  // 現在のタブでベースエリア3種のマスクが揃っているかチェック
  const currentTabHasAllMasks = currentTabImages ? hasAllMaskImages(currentTabImages) : true
  const shouldStartPolling = !currentTabHasAllMasks

  // 現在選択中の画像IDを取得
  const currentImageId = currentTabImages?.id

  // checkMaskReady に現在の画像IDをバインドしたコールバック
  // useCallbackでメモ化しないと毎回新しい関数が作成され、useMaskPollingが無限ループする
  const boundCheckMaskReady = useCallback(() => {
    if (!currentImageId || !checkMaskReady) {
      return Promise.reject(new Error('No image ID or checkMaskReady'))
    }
    return checkMaskReady(currentImageId)
  }, [currentImageId, checkMaskReady])

  // ポーリング完了時に取得したマスクURL（dataを更新せずにマスクURLのみ取得）
  const [polledMaskUrls, setPolledMaskUrls] = useState<Record<string, string> | null>(null)

  // マスクポーリング完了時にマスクURLのみを取得するハンドラ
  const handleMaskReady = useCallback(async () => {
    if (!fetchMaskUrls || !currentImageId) return
    try {
      const urls = await fetchMaskUrls(currentImageId)
      setPolledMaskUrls(urls)
    } catch (error) {
      console.error('[SimulationDetail] マスクURL取得エラー:', error)
    }
  }, [fetchMaskUrls, currentImageId])

  // マスクポーリングのhook
  const { phase: maskPollingPhase } = useMaskPolling({
    shouldStartPolling,
    checkMaskReady: currentImageId && checkMaskReady ? boundCheckMaskReady : undefined,
    externalMaskReady: currentTabHasAllMasks,
    onMaskReady: handleMaskReady,
  })

  useLayerEditingStateSync({
    activeTab,
    openLayerId,
    setOpenLayerId,
    editingArea,
    setEditingArea,
    subAreas: subAreaSelection.subAreas,
    excludeAreas: excludeAreaSelection.excludeAreas,
  })

  const thumbnailGroups = useThumbnailGroups({
    images,
    tabs,
    activeTab,
    selectedProcessedIndexByTab,
    staffName: data.staffName,
    customerName: data.customerName,
    customerNameFull: data.customerNameFull,
    viewerRole: role,
  })

  // 履歴の絞り込み（すべて／ピン留め／お気に入り）とピン留めの一覧（issue #2953）
  const {
    filter: historyFilter,
    setFilter: setHistoryFilter,
    counts: historyCounts,
    filteredGroups: filteredThumbnailGroups,
    pinnedItems,
  } = useHistoryFilter(thumbnailGroups)
  // 上限で押したときに入れ替え相手を選ぶダイアログの対象（design.md D2）
  const [pinReplaceTarget, setPinReplaceTarget] = useState<ThumbnailItem | null>(null)
  // 施主のタイトル編集シートの対象（決定事項1）
  const [titleSheetTarget, setTitleSheetTarget] = useState<ThumbnailItem | null>(null)
  // 共有ダイアログの状態（アンカーの履歴、プロジェクト単位にした理由、有効期限）
  const [shareAnchorImageId, setShareAnchorImageId] = useState<number | null>(null)
  const [shareFallbackReason, setShareFallbackReason] = useState<ShareFallbackReason | undefined>(
    undefined,
  )
  const [shareExpiresAt, setShareExpiresAt] = useState<Date | undefined>(undefined)
  const [sharePinPending, setSharePinPending] = useState(false)

  /** 全カットの履歴（生成画像）から imageId で引く */
  const findThumbnailByImageId = useCallback(
    (imageId: bigint | number | undefined): ThumbnailItem | undefined => {
      if (imageId === undefined) return undefined
      const id = Number(imageId)
      for (const group of thumbnailGroups) {
        const found = group.items.find(item => item.type === 'processed' && item.imageId === id)
        if (found) return found
      }
      return undefined
    },
    [thumbnailGroups],
  )

  /** ピンの付け外し。上限で付けようとしたら入れ替えダイアログを開く（エラーで止めない） */
  const handleTogglePin = useCallback(
    async (item: ThumbnailItem) => {
      if (!onUpdateImagePin || !item.imageId) return
      if (!item.pinnedAt && pinnedItems.length >= AI_COLOR_PIN_LIMIT) {
        setPinReplaceTarget(item)
        return
      }
      try {
        await onUpdateImagePin(item.imageId, !item.pinnedAt)
      } catch (error) {
        handleError(error)
      }
    },
    [onUpdateImagePin, pinnedItems.length, handleError],
  )

  const handleSaveTitle = useCallback(
    async (imageId: number, title: string) => {
      if (!onUpdateImageTitle) return
      try {
        await onUpdateImageTitle(imageId, title)
      } catch (error) {
        handleError(error)
        throw error
      }
    },
    [onUpdateImageTitle, handleError],
  )

  // 編集中エリアの「実効プリセット選択」.
  // state にコピーせず毎回導出する（初期値を state へ流し込む useEffect を置くと、
  // baseAreaDefaultPresetIds が後から届いたときに 'all' が確定して永久にデフォルトが
  // 効かなくなる）. Select の表示値とパレットに渡す色は必ずこの 1 つの値を共有する.
  const effectivePresetId = useMemo(() => {
    if (!editingArea) {
      return ALL_COLORS_SENTINEL
    }
    return resolveEffectivePresetId(
      editingArea,
      userSelectedPresetByArea,
      baseAreaDefaultPresetIds,
      colorPresets,
    )
  }, [editingArea, userSelectedPresetByArea, baseAreaDefaultPresetIds, colorPresets])

  const handlePresetChange = (presetId: string | typeof ALL_COLORS_SENTINEL) => {
    if (!editingArea) {
      return
    }
    setUserSelectedPresetByArea(prev => ({ ...prev, [editingArea]: presetId }))
  }

  const handleColorSelect = (colorValue: string) => {
    if (!editingArea) {
      return
    }
    // サブエリアの色選択
    if (editingArea.startsWith('subarea-')) {
      const idx = Number.parseInt(editingArea.replace('subarea-', ''), 10)
      subAreaSelection.setSubAreaColor(idx, colorValue)
      return
    }
    // ベースエリアの色選択。色 state を更新し、active mode を 'color' にする.
    // 過去に選択した素材 state は保持する（タブ切替で素材を再表示できるようにするため）.
    setAreaColors(prev => ({ ...prev, [editingArea]: colorValue }))
    setAreaSelectionMode(prev => ({ ...prev, [editingArea]: 'color' }))
  }

  // 屋根・外壁ベースエリアにフォーカスしている場合の MaterialPalette 用 targetPart を返す.
  const materialTargetPart: MaterialTargetPart | undefined =
    editingArea === '外壁' ? 'wall' : editingArea === '屋根' ? 'roof' : undefined

  const handleMaterialSelect = (materialId: bigint) => {
    if (!editingArea || !materialTargetPart) {
      return
    }
    // 素材 state を更新し、active mode を 'material' にする.
    // 過去に選択した色 state は保持する（タブ切替で色を再表示できるようにするため）.
    setAreaMaterials(prev => ({ ...prev, [editingArea]: materialId }))
    setAreaSelectionMode(prev => ({ ...prev, [editingArea]: 'material' }))
  }

  const handleCustomAreaSettingSave = (payload: {
    label: string
    useDetectedAreaOnly: boolean
    mergeTargets: number[]
    mergeIntoBaseArea: string | null
  }) => {
    if (editingCustomAreaIndex === null) {
      return
    }

    const { label, useDetectedAreaOnly, mergeTargets, mergeIntoBaseArea } = payload
    const targetIndex = editingCustomAreaIndex

    // ベースエリアに統合（issue #2224）: カスタムエリアのポリゴンを対象ベースエリアへ移し、
    // カスタムエリア自体は削除する。以降そのベースエリアの色・素材設定が反映される。
    // 注: setState updater は純粋関数でなければならない（StrictMode / concurrent で複数回呼ばれ得る）ため、
    // 移動対象ポリゴンの収集と setMergedPolygonsByara は updater の外で行う。
    if (mergeIntoBaseArea) {
      const target = subAreaSelection.subAreas[targetIndex]
      if (!target) {
        handleCustomAreaSettingOpenChange(false)
        return
      }
      const movedPolygons: (typeof target.points)[] = []
      if (target.points.length >= 3) movedPolygons.push(target.points)
      for (const poly of target.polygons ?? []) {
        if (poly.length >= 3) movedPolygons.push(poly)
      }
      // 有効なポリゴンが無い（描画途中など）場合は何も統合できないため、
      // カスタムエリアを削除せずそのまま残す（サイレントな消失を防ぐ）。
      if (movedPolygons.length === 0) {
        handleCustomAreaSettingOpenChange(false)
        return
      }
      setMergedPolygonsByArea(prevMerged => ({
        ...prevMerged,
        [mergeIntoBaseArea]: [...(prevMerged[mergeIntoBaseArea] ?? []), ...movedPolygons],
      }))
      subAreaSelection.setSubAreas(prev => prev.filter((_, idx) => idx !== targetIndex))
      subAreaSelection.setSelectedSubAreaIndex(0)
      handleCustomAreaSettingOpenChange(false)
      return
    }
    const mergeSet = new Set(mergeTargets)
    const removedBefore = mergeTargets.filter(idx => idx < targetIndex).length
    const nextIndex = targetIndex - removedBefore

    subAreaSelection.setSubAreas(prev => {
      const target = prev[targetIndex]
      if (!target) {
        return prev
      }

      const mergedPolygons = mergeTargets.flatMap(idx => {
        const area = prev[idx]
        if (!area) return []
        const polygons = area.polygons ? [...area.polygons] : []
        if (area.points.length > 0) {
          polygons.unshift(area.points)
        }
        return polygons
      })

      const updatedTarget = {
        ...target,
        label: label || target.label || `カスタムエリア${targetIndex + 1}`,
        useDetectedAreaOnly,
        polygons: [...(target.polygons ?? []), ...mergedPolygons],
      }

      const nextAreas = prev.filter((_, idx) => idx === targetIndex || !mergeSet.has(idx))
      nextAreas.splice(nextIndex, 1, updatedTarget)
      return nextAreas
    })

    subAreaSelection.setSelectedSubAreaIndex(nextIndex)
    handleCustomAreaSettingOpenChange(false)
  }

  const handleCustomAreaSettingOpenChange = (open: boolean) => {
    if (!open) {
      setIsCustomAreaSettingOpen(false)
      setEditingCustomAreaIndex(null)
      return
    }
    setIsCustomAreaSettingOpen(true)
  }

  const handleShareDialogOpenChange = (open: boolean) => {
    setIsShareDialogOpen(open)
    if (!open) {
      return
    }
    setShareUrl(undefined)
    setShareError(undefined)
    setShareExpiresAt(undefined)
    // 「いま選択している履歴」の判定（design.md D5）。
    // 1. 履歴を選択していて未実行の変更が無い → その履歴をアンカーにする
    // 2. 履歴を選択しているが未実行の変更がある → プロジェクト単位（理由: unexecutedChanges）
    // 3. 現況画像を選択していて履歴を選んでいない → プロジェクト単位（理由: noHistorySelected）
    const anchorItem =
      selectedProcessedImage && !hasFormChanged
        ? findThumbnailByImageId(selectedProcessedImage.id)
        : undefined
    setShareAnchorImageId(anchorItem?.imageId ?? null)
    setShareFallbackReason(
      anchorItem ? undefined : selectedProcessedImage ? 'unexecutedChanges' : 'noHistorySelected',
    )
    if (!createShareLink) {
      setShareError('共有リンクの生成が利用できません。')
      return
    }
    if (!currentImageId) {
      setShareError('共有リンクの生成に必要な画像が見つかりません。')
      return
    }
    // 共有リンクのプリセット選択は作成時点で固定される不変スナップショットのため、
    // 未ロードのまま作成するとデフォルトが欠けた選択（全ベースエリア "__ALL__"）が恒久的に焼き付き、
    // 施主に見せる色が広がってしまう。undefined = 未ロードのときだけ弾く.
    // `colorPresets: []`（プリセット 0 件の企業）/ `baseAreaDefaultPresetIds: {}`（デフォルト未設定の
    // 企業）はいずれも正当なロード済み状態なので弾かない（弾くと共有リンクを作れなくなる）.
    if (colorPresets === undefined || baseAreaDefaultPresetIds === undefined) {
      setShareError('色プリセットの読み込みが完了していません。少し待ってからお試しください。')
      return
    }
    setIsShareLinkLoading(true)
    const parameters = buildProcessParameters()
    // 共有時点のエリア別プリセット選択を施主拘束用に確定する.
    // 未選択（一度もダイアログを開いていない）エリアも、ベースはデフォルト、カスタムは "__ALL__" で必ず埋める.
    const areaPresetSelection = buildAreaPresetSelection(
      userSelectedPresetByArea,
      baseAreaDefaultPresetIds,
      subAreaSelection.subAreas.length,
    )
    void createShareLink({
      baseImageId: Number(currentImageId),
      parameters,
      areaPresetSelection,
      anchorImageId: anchorItem?.imageId,
    })
      .then(result => {
        if (typeof result === 'string') {
          setShareUrl(result)
          return
        }
        setShareUrl(result.url)
        setShareExpiresAt(result.expiresAt)
      })
      .catch(error => {
        console.error('[SimulationDetail] 共有リンク生成エラー:', error)
        setShareError('共有リンクの生成に失敗しました。')
      })
      .finally(() => {
        setIsShareLinkLoading(false)
      })
  }

  /**
   * マスク再生成の回数上限（決定 57 / 79 C6）に達したベースエリア。
   *
   * キーは「元画像 ID:部位」。上限はサーバーがこの単位で数えるため、1 部位が上限に
   * 達しても他の部位・他のカットの再生成は止めない。平常時は何も出さず、
   * 上限に達した部位だけメニュー項目を無効化する（エラーメッセージは既存の
   * `maskAdjustError` が 1 行で出す）。
   */
  const [maskRegenerationLimitedKeys, setMaskRegenerationLimitedKeys] = useState<string[]>([])
  const currentImageMaskLimitedAreas = useMemo(
    () =>
      maskRegenerationLimitedKeys
        .filter(key => key.startsWith(`${currentImage?.id ?? ''}:`))
        .map(key => key.slice(key.indexOf(':') + 1)),
    [maskRegenerationLimitedKeys, currentImage?.id],
  )

  /**
   * ベースエリアのマスク再生成をリクエスト
   */
  const handleBaseAreaAdjust = async (area: string) => {
    if (!currentImage?.url || !regenerateMask) {
      return
    }
    const maskType = AREA_MASK_TYPE_MAP[area]
    if (!maskType) {
      return
    }
    // currentImage.id は bigint（proto int64）。DB ID として Number() で変換する。
    // 画像 ID は safe integer 範囲（53ビット以内）を前提とするため精度損失は生じない。
    if (!currentImage.id) {
      // 無言 no-op にせずユーザーへフィードバックする（データ再取得で復旧し得るため）
      setMaskAdjustError(
        '対象画像の情報を取得できませんでした。ページを再読み込みして再試行してください。',
      )
      return
    }
    const baseImageId = Number(currentImage.id)
    setMaskAdjustError(undefined)
    try {
      await regenerateMask(currentImage.url, maskType, baseImageId)
    } catch (error) {
      console.error('[SimulationDetail] マスク再生成エラー:', error)
      // 再生成の回数上限に達した場合は、時間をおいても解消しない。汎用の
      // 「時間をおいて再試行」を出すと誤った案内になるため、サーバーの文言をそのまま見せる。
      const structured = parseStructuredError(error)
      const limitExceeded = structured.code === ERROR_CODES.MASK_REGENERATION_LIMIT_EXCEEDED
      if (limitExceeded) {
        // 上限は時間をおいても解消しないので、以後この部位の再生成メニューを出さない
        const key = `${baseImageId}:${area}`
        setMaskRegenerationLimitedKeys(keys => (keys.includes(key) ? keys : [...keys, key]))
      }
      setMaskAdjustError(
        limitExceeded
          ? structured.message
          : 'エリアの再生成に失敗しました。時間をおいて再試行してください。',
      )
    }
  }

  const hasValidSubAreas = useMemo(
    () =>
      subAreaSelection.subAreas.some(
        area =>
          area.color && (area.points.length >= 3 || area.polygons?.some(poly => poly.length >= 3)),
      ),
    [subAreaSelection.subAreas],
  )

  const buildProcessParameters = useCallback(() => {
    return buildProcessImageParameters({
      areaColors,
      hasValidSubAreas,
      subAreas: subAreaSelection.subAreas,
      excludeAreas: excludeAreaSelection.excludeAreas,
      unifyColor,
      availableColors: availableColors ?? [],
      areaMaterials,
      areaSelectionMode,
      // 取得済み素材一覧に存在する ID だけを送る。削除済みなどで一覧から消えた素材が
      // 状態に残っていても（UI には未アサインに見える）、暗黙的にリクエストへ載せない。
      availableMaterialIds: new Set((availableMaterials ?? []).map(m => m.id)),
      // ベースにした結果画像の Parameters。availableColors に無い履歴色コードの
      // RGB を、この Parameters から引き継ぐ（miss 時のみ 0,0,0 に落ちる）。
      baseParameters: selectedProcessedImage?.parameters,
      // カスタムエリアからベースエリアに統合されたポリゴン（issue #2224）
      mergedPolygonsByArea,
    })
  }, [
    areaColors,
    areaMaterials,
    areaSelectionMode,
    availableColors,
    availableMaterials,
    excludeAreaSelection.excludeAreas,
    hasValidSubAreas,
    mergedPolygonsByArea,
    selectedProcessedImage?.parameters,
    subAreaSelection.subAreas,
    unifyColor,
  ])

  const handleProcessImage = async () => {
    if (!processImageMutation) {
      return
    }

    const currentActiveImage = images.find(img => img.id?.toString() === activeTab)
    if (!currentActiveImage || !currentActiveImage.id) {
      return
    }

    const parameters = buildProcessParameters()

    // ProcessImageRequestを構築（IDベース、oneof使用）
    const request = create(ProcessImageRequestSchema, {
      imageSource: {
        case: 'imageId',
        value: currentActiveImage.id,
      },
      parameters,
    })

    try {
      await processImageMutation.mutateAsync(request)
      // 実行成功後、エリアへのフォーカスを解除（オーバーレイ・カラーパレットも消える）
      handleDismissColorPalette()
      if (isSubAreaModeActive) {
        setIsSubAreaModeActive(false)
      }
      if (excludeAreaSelection.excludeEnabled) {
        excludeAreaSelection.setExcludeEnabled(false)
      }
      // 拡大・縮小をリセットして原寸に戻す
      originalZoomRef.current?.resetTransform()
      processedZoomRef.current?.resetTransform()
      // 注意: mutation成功後、onSuccessでquery invalidationが実行されるが、
      // それは非同期処理なので、この時点ではまだimages配列は更新されていない。
      // 画像選択ロジックはuseProcessImageのonSuccess（refetch完了後）で実行される
    } catch (error) {
      handleError(error)
    }
  }

  // マスク準備中かどうか（初期待機中 or ポーリング中）
  const isMaskPreparing =
    isMaskRegenerating || maskPollingPhase === 'initial' || maskPollingPhase === 'polling'

  // 実行可能かどうか（ベースエリアの色 / 素材 / 統合ポリゴン、またはサブエリアの色が
  // 1つ以上指定されていて、かつ変更があり、マスク準備中でない）。
  // 統合ポリゴンのみのケースも実行可能にする（色付きカスタムエリアを統合で消した直後に
  // 再実行できないと、旧カスタム色を落とした画像を生成できなくなるため）。
  const canProcessImage =
    (Object.keys(areaColors).length > 0 ||
      Object.keys(areaMaterials).length > 0 ||
      hasValidSubAreas ||
      hasAnyMergedPolygons(mergedPolygonsByArea)) &&
    !processing &&
    hasFormChanged &&
    !isMaskPreparing

  const showProcessedImage = Boolean(selectedProcessedImage?.url)
  const isSubAreaOverlayOnProcessed = isEditMode && isSubAreaModeActive && !showOriginalImage

  // ── エリア編集キャンバスの操作モード（選択 / キャンバス移動）とツールバー ──
  const isSubAreaEditing = Boolean(
    originalDimensions && isEditMode && isSubAreaModeActive && permissions.canEditCustomArea,
  )
  const isExcludeEditing = Boolean(
    originalDimensions && isEditMode && excludeAreaSelection.excludeEnabled,
  )
  const canvasEditingActive = isSubAreaEditing || isExcludeEditing

  // 頂点右クリックの削除メニュー
  const [vertexMenu, setVertexMenu] = useState<{
    x: number
    y: number
    context: 'subarea' | 'exclude'
    pointIndex: number
    polygonIndex: number
  } | null>(null)

  // パネルごとのズーム倍率 (頂点マーカー・ヒット領域のスクリーン一定サイズ補正用)。
  // パン中は scale が変わらず setState が同値 bail するため再レンダーは発生しない
  const [originalZoomScale, setOriginalZoomScale] = useState(1)
  const [processedZoomScale, setProcessedZoomScale] = useState(1)

  // プレビューへ切り替えたとき、編集中に等倍より縮小していた分を原寸へ戻す（issue #2417）。
  // minScale を上げるだけでは今の transform は変わらないため、縮小した状態のまま
  // プレビューに入れてしまう（= 縮小操作を提供しない前提と食い違う）。
  //
  // プレビューに入ったタイミングで 1 度だけ実行する。ZoomableImage の onScaleChange は
  // ライブラリの onTransformed 配線なので resetTransform のアニメーション中も発火する。
  // ガード無しで倍率を deps に入れると「中間倍率で再レンダー → まだ 1 未満なので
  // resetTransform を呼び直す」をフレームごとに繰り返し、アニメーションが収束しない。
  const previewZoomResetDoneRef = useRef(false)
  useEffect(() => {
    if (!isPreviewMode) {
      previewZoomResetDoneRef.current = false
      return
    }
    if (previewZoomResetDoneRef.current) return
    previewZoomResetDoneRef.current = true
    if (originalZoomScale < AI_COLOR_PREVIEW_MIN_ZOOM_SCALE) {
      originalZoomRef.current?.resetTransform()
    }
    if (processedZoomScale < AI_COLOR_PREVIEW_MIN_ZOOM_SCALE) {
      processedZoomRef.current?.resetTransform()
    }
  }, [isPreviewMode, originalZoomScale, processedZoomScale])

  /**
   * カスタムエリアと除外エリアの編集が同時に有効なことがあるため、
   * undo / Delete のキーボード操作は「最後に操作したコンテキスト」に届ける。
   * (除外エリア固定優先だと、カスタムエリア編集中の ⌘Z が除外エリアを巻き戻す)
   */
  const [lastEditContext, setLastEditContext] = useState<'subarea' | 'exclude'>('subarea')
  const resolveEditContext = (): 'subarea' | 'exclude' => {
    if (isSubAreaEditing && isExcludeEditing) return lastEditContext
    return isExcludeEditing ? 'exclude' : 'subarea'
  }

  const undoContext = (context: 'subarea' | 'exclude') => {
    // 履歴の巻き戻しで頂点 index がズレるため、開いているメニューは閉じる
    setVertexMenu(null)
    if (context === 'exclude') {
      excludeAreaSelection.undo()
    } else {
      subAreaSelection.undo()
    }
  }
  const handleCanvasUndo = () => {
    undoContext(resolveEditContext())
  }
  const handleCanvasDeleteSelected = () => {
    // 削除で頂点 index がズレるため、開いているメニューは閉じる
    setVertexMenu(null)
    const context = resolveEditContext()
    const primary = context === 'exclude' ? 'exclude' : 'subarea'
    // 優先コンテキストに選択が無ければ、選択を持つもう一方へフォールバック
    if (primary === 'exclude' && excludeAreaSelection.selectedVertexIndex !== null) {
      excludeAreaSelection.deleteSelectedVertex()
      return
    }
    if (primary === 'subarea' && isSubAreaEditing && subAreaSelection.selectedVertex) {
      subAreaSelection.deleteSelectedVertex()
      return
    }
    if (isExcludeEditing && excludeAreaSelection.selectedVertexIndex !== null) {
      excludeAreaSelection.deleteSelectedVertex()
      return
    }
    if (isSubAreaEditing && subAreaSelection.selectedVertex) {
      subAreaSelection.deleteSelectedVertex()
    }
  }
  const canvasToolMode = useCanvasToolMode({
    enabled: canvasEditingActive,
    onUndo: handleCanvasUndo,
    onDeleteSelected: handleCanvasDeleteSelected,
  })
  const isCanvasPanMode = canvasToolMode.effectiveMode === 'pan'

  const handleSubAreaVertexContextMenu = (
    pointIndex: number,
    polygonIndex: number,
    clientX: number,
    clientY: number,
  ) => {
    subAreaSelection.selectVertex(pointIndex, polygonIndex)
    setVertexMenu({ x: clientX, y: clientY, context: 'subarea', pointIndex, polygonIndex })
  }
  const handleExcludeVertexContextMenu = (
    pointIndex: number,
    _polygonIndex: number,
    clientX: number,
    clientY: number,
  ) => {
    excludeAreaSelection.selectVertex(pointIndex)
    setVertexMenu({ x: clientX, y: clientY, context: 'exclude', pointIndex, polygonIndex: -1 })
  }

  // タブ切替・生成画像の切替時は undo 履歴を破棄する。
  // useFormParametersSync が selectedProcessedIndexByTab の変化でも保存パラメータから
  // subAreas / excludeAreas を復元するため、復元をまたいだ undo (別画像のポリゴンへの
  // 巻き戻し) を防ぐには両方に連動させる必要がある
  const resetSubAreaHistory = subAreaSelection.resetHistory
  const resetExcludeHistory = excludeAreaSelection.resetHistory
  useEffect(() => {
    resetSubAreaHistory()
    resetExcludeHistory()
    setVertexMenu(null)
  }, [activeTab, selectedProcessedIndexByTab, resetSubAreaHistory, resetExcludeHistory])

  /** パネルごとに undo 対象コンテキストを固定したツールバーを生成する */
  const renderCanvasToolbar = (context: 'subarea' | 'exclude') => (
    <FloatingToolbar
      canUndo={context === 'exclude' ? excludeAreaSelection.canUndo : subAreaSelection.canUndo}
      className="absolute right-2 top-2 z-10"
      mode={canvasToolMode.effectiveMode}
      onModeChange={canvasToolMode.setMode}
      onUndo={() => undoContext(context)}
    />
  )

  // 施主側: カスタムエリアにフォーカス中かどうか（編集権限なしでもフォーカス可能）
  const isCustomerSubAreaFocused =
    role === 'customer' && isSubAreaModeActive && subAreaSelection.subAreas.length > 0

  // 施主側: フォーカス中のカスタムエリアのデータを取得（ポリゴン + useDetectedAreaOnlyフラグ）
  const focusedSubAreaData = useMemo(() => {
    if (!isCustomerSubAreaFocused) return null
    const idx = subAreaSelection.selectedSubAreaIndex
    const area = subAreaSelection.subAreas[idx]
    if (!area) return null
    // メインポリゴン（points）と追加ポリゴン（polygons）を結合
    const polygons: { originalX: number; originalY: number }[][] = []
    if (area.points.length >= 3) {
      polygons.push(area.points)
    }
    if (area.polygons) {
      for (const poly of area.polygons) {
        if (poly.length >= 3) {
          polygons.push(poly)
        }
      }
    }
    return {
      polygons,
      useDetectedAreaOnly: area.useDetectedAreaOnly ?? false,
    }
  }, [isCustomerSubAreaFocused, subAreaSelection.selectedSubAreaIndex, subAreaSelection.subAreas])

  // 施主側: 全カスタムエリア・除外エリアのポリゴンを取得（ベースエリアフォーカス時にくり抜くため）
  const allExcludePolygons = useMemo(
    () =>
      buildExcludePolygonsForCustomer(
        role,
        subAreaSelection.subAreas,
        excludeAreaSelection.excludeAreas,
      ),
    [role, subAreaSelection.subAreas, excludeAreaSelection.excludeAreas],
  )

  const { download: handleDownload, isDownloading } = useImageDownload({
    url: selectedProcessedImage?.url,
    filenamePrefix: data.dealName ?? '生成画像',
    onError: () => alert('画像のダウンロードに失敗しました。時間をおいて再度お試しください。'),
  })

  // サマリ付き画像の DL は、表示中の結果画像が生成された時の parameters から組み立てる
  // （編集中フォーム state ではなく selectedProcessedImage.parameters を参照）
  const { download: handleDownloadSummary, isDownloading: isSummaryDownloading } =
    useSummaryImageDownload({
      beforeImageUrl: currentImage?.url,
      afterImageUrl: selectedProcessedImage?.url,
      parameters: selectedProcessedImage?.parameters,
      projectName: data.dealName ?? 'プロジェクト',
      availableColors: availableColors ?? [],
      availableMaterials: availableMaterials ?? [],
      companyName,
      companyLogoUrl,
      filenamePrefix: data.dealName ?? '生成画像',
      onError: () => alert('サマリ画像の生成に失敗しました。時間をおいて再度お試しください。'),
    })

  // マスク画像URLを抽出（PoC: マスクカラーオーバーレイ用）
  // polledMaskUrlsをマージすることで、data更新なしでオーバーレイ表示を可能にする
  const currentMaskUrls = useMemo(() => {
    const fromData = getMaskUrls(currentImage)
    // ポーリングで取得したURLで上書き（dataが更新されていないケースに対応）
    if (polledMaskUrls) {
      return { ...fromData, ...polledMaskUrls }
    }
    return fromData
  }, [currentImage, polledMaskUrls])

  // 雨樋・破風を除外したベースエリアマスクURL（カスタムエリアのAND演算用）
  // SSOT: ((外壁 ∪ 屋根) ∩ ポリゴン) ∩ ¬雨樋 ∩ ¬破風
  const maskUrlsForCustomArea = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 除外目的の destructuring
    const { 雨樋: _gutter, '破風・鼻隠し': _fascia, ...rest } = currentMaskUrls
    return rest
  }, [currentMaskUrls])

  // 外壁・屋根に統合されたポリゴン（issue #2224）。カスタムエリアオーバーレイの
  // ベース（外壁∪屋根）へ union する。Python は augmented マスクで ROI を計算するため、
  // これを渡さないとオーバーレイと色変換範囲が一致しない（特に統合解除の再描画フロー）。
  const baseMergedPolygonsForCustomArea = useMemo(
    () => [...(mergedPolygonsByArea['外壁'] ?? []), ...(mergedPolygonsByArea['屋根'] ?? [])],
    [mergedPolygonsByArea],
  )

  const selectedColorInPalette = useMemo(() => {
    if (!editingArea) {
      return undefined
    }

    if (editingArea.startsWith('subarea-')) {
      const index = Number.parseInt(editingArea.replace('subarea-', ''), 10)
      return subAreaSelection.subAreas[index]?.color
    }

    return areaColors[editingArea]
  }, [editingArea, subAreaSelection.subAreas, areaColors])

  const selectedMaterialIdInPalette = useMemo(() => {
    if (!editingArea) return undefined
    return areaMaterials[editingArea]
  }, [editingArea, areaMaterials])

  // 色指定ダイアログに渡す色一覧をエリア別に解決する.
  // - 企業: 実効プリセット（effectivePresetId）から解決（'all' → 全色）.
  //   Select の表示値と同一の値を使うため、表示と色候補が乖離しない.
  // - 施主: 企業指定に拘束される。editingArea を area_key に変換して availableColorsByArea から引く
  // どちらもフォールバックは availableColors（全色）.
  const dialogColors = useMemo(() => {
    if (!editingArea) {
      return availableColors ?? []
    }
    if (role === 'customer') {
      if (!availableColorsByArea) {
        return availableColors ?? []
      }
      // カスタムエリアの area_key は "<base_image_id>:<index>" 形式（カット単位で名前空間が分かれる）
      const areaKey = editingAreaToAreaKey(editingArea, currentImage?.id)
      return availableColorsByArea[areaKey] ?? availableColors ?? []
    }
    return resolveDialogColors(effectivePresetId, colorPresets, availableColors ?? [])
  }, [
    editingArea,
    role,
    availableColorsByArea,
    availableColors,
    currentImage?.id,
    effectivePresetId,
    colorPresets,
  ])
  // editingArea（カラーパレット表示中）があればそれを優先、なければ openLayerId からベースエリア名を復元
  const focusedBaseArea = useMemo(() => {
    if (editingArea) {
      return editingArea.startsWith('subarea-') ? null : editingArea
    }
    if (openLayerId?.startsWith('base-')) {
      return openLayerId.replace('base-', '')
    }
    return null
  }, [editingArea, openLayerId])
  // 施主画面では日時に時刻を出さない（企業が遅い時間に作業した時刻を見せない。藤井の決定）。
  // 企業画面は従来どおり時刻あり
  const formatViewerDate = useCallback(
    (date: Date | undefined) =>
      role === 'customer' ? formatHistoryDate(date) : formatTimestamp(date),
    [role],
  )
  const controlBarTitle = useMemo(() => {
    if (!currentTab) {
      return isEditMode ? '編集中' : 'プレビュー中'
    }
    const dateLabel = currentImage?.uploadedAt
      ? ` / ${formatViewerDate(timestampDate(currentImage.uploadedAt))}`
      : ''
    return `外観カット${currentTab.index + 1}${dateLabel}を${
      isEditMode ? '編集中' : 'プレビュー中'
    }`
  }, [currentTab, currentImage?.uploadedAt, isEditMode, formatViewerDate])
  const previewLabelClass = 'rounded-full bg-[#111827] px-2 py-0.5 text-[10px] text-white shadow-sm'
  const originalSlot = showOriginalImage
    ? {
        containerRef: originalImageContainerRef,
        heightScale: originalZoomScale,
        contentHeight: originalContainerSize.height,
        content: currentImage?.url ? (
          <ZoomableImage
            key={activeTab}
            ref={originalZoomRef}
            src={currentImage.url}
            alt={`外観カット ${currentTab ? currentTab.index + 1 : 1}`}
            isDragging={subAreaSelection.isDragging}
            maxScale={AI_COLOR_MAX_ZOOM_SCALE}
            minScale={canvasMinZoomScale}
            panDisabled={isSubAreaEditing && !isCanvasPanMode}
            grabCursor={isSubAreaEditing && isCanvasPanMode}
            onScaleChange={setOriginalZoomScale}
            onLoad={handleImageLoad}
          >
            {/* ベースエリアフォーカス時のオーバーレイ */}
            {originalDimensions && isEditMode && !isMaskPreparing && focusedBaseArea && (
              <MaskColorOverlay
                maskUrls={currentMaskUrls}
                focusedArea={focusedBaseArea}
                getMetrics={getOriginalMetrics}
                // 雨樋・破風フォーカス時はカスタムエリアを切り抜かない（最高優先のため）
                excludePolygons={
                  focusedBaseArea === '雨樋' || focusedBaseArea === '破風・鼻隠し'
                    ? undefined
                    : allExcludePolygons
                }
                // 当該ベースエリアに統合されたカスタムエリア領域を union 表示（issue #2224）
                includePolygons={mergedPolygonsByArea[focusedBaseArea]}
                originalDimensions={originalDimensions}
              />
            )}
            {/* 企業側: カスタムエリアフォーカス時のSVGオーバーレイ（編集可能） */}
            {originalDimensions &&
              isEditMode &&
              isSubAreaModeActive &&
              permissions.canEditCustomArea && (
                <div onPointerDownCapture={() => setLastEditContext('subarea')}>
                  <AreaOverlay
                    areas={subAreaSelection.subAreas}
                    draggingPointIndex={subAreaSelection.draggingPointIndex}
                    draggingPolygonIndex={subAreaSelection.draggingPolygonIndex}
                    getMetrics={getSubAreaMetrics}
                    interactionDisabled={isCanvasPanMode}
                    isDragging={subAreaSelection.isDragging}
                    onDeletePoint={subAreaSelection.handleDeletePoint}
                    onDragEnd={subAreaSelection.handleDragEnd}
                    onDragStart={subAreaSelection.handleDragStart}
                    onInsertPoint={info =>
                      subAreaSelection.handleInsertPoint(
                        info.edgeIndex,
                        info.originalX,
                        info.originalY,
                        info.polygonIndex,
                      )
                    }
                    onOverlayClick={subAreaSelection.handleOverlayClick}
                    onOverlayMove={subAreaSelection.handleOverlayMove}
                    onVertexContextMenu={handleSubAreaVertexContextMenu}
                    originalDimensions={originalDimensions}
                    selectedAreaIndex={subAreaSelection.selectedSubAreaIndex}
                    selectedVertex={subAreaSelection.selectedVertex}
                    zoomScale={originalZoomScale}
                  />
                </div>
              )}
            {/* 施主側: カスタムエリアフォーカス時のパターンオーバーレイ（表示のみ） */}
            {originalDimensions &&
              isEditMode &&
              isCustomerSubAreaFocused &&
              focusedSubAreaData &&
              focusedSubAreaData.polygons.length > 0 && (
                <PolygonMaskOverlay
                  polygons={focusedSubAreaData.polygons}
                  originalDimensions={originalDimensions}
                  getMetrics={getOriginalMetrics}
                  useDetectedAreaOnly={focusedSubAreaData.useDetectedAreaOnly}
                  baseMaskUrls={maskUrlsForCustomArea}
                  // 雨樋・破風色指定がある場合のみ除外。未指定ならカスタム色で塗り潰し可能（真理表に準拠）
                  gutterMaskUrl={areaColors['雨樋'] ? currentMaskUrls['雨樋'] : undefined}
                  fasciaMaskUrl={
                    areaColors['破風・鼻隠し'] ? currentMaskUrls['破風・鼻隠し'] : undefined
                  }
                  // ベースエリアに統合されたポリゴン（issue #2224）。Python の augmented マスクと演算を揃える
                  baseMergedPolygons={baseMergedPolygonsForCustomArea}
                  gutterMergedPolygons={
                    areaColors['雨樋'] ? mergedPolygonsByArea['雨樋'] : undefined
                  }
                  fasciaMergedPolygons={
                    areaColors['破風・鼻隠し'] ? mergedPolygonsByArea['破風・鼻隠し'] : undefined
                  }
                />
              )}
          </ZoomableImage>
        ) : (
          <div className="flex items-center justify-center w-full h-full text-sm text-slate-400">
            画像が登録されていません
          </div>
        ),
        badge: isPreviewMode ? (
          <span className={`absolute inline-flex items-center left-4 top-4 ${previewLabelClass}`}>
            現況画像
          </span>
        ) : (
          <span className="absolute inline-flex items-center px-4 py-2 text-xs font-semibold text-white rounded-full shadow pointer-events-none left-4 top-4 bg-slate-900/85">
            現況画像
          </span>
        ),
        toolbar: isSubAreaEditing ? renderCanvasToolbar('subarea') : undefined,
        zoomControl: (
          <ZoomControls
            maxScale={AI_COLOR_MAX_ZOOM_SCALE}
            minScale={canvasMinZoomScale}
            onReset={() => originalZoomRef.current?.resetTransform()}
            onZoomIn={() => originalZoomRef.current?.zoomIn(AI_COLOR_ZOOM_STEP)}
            onZoomOut={
              canZoomOutCanvas
                ? () => originalZoomRef.current?.zoomOut(AI_COLOR_ZOOM_STEP)
                : undefined
            }
            scale={originalZoomScale}
          />
        ),
      }
    : undefined
  const processedSlot =
    showProcessedImage && selectedProcessedImage
      ? {
          containerRef: processedImageContainerRef,
          heightScale: processedZoomScale,
          contentHeight: processedContainerSize.height,
          content: (
            <ZoomableImage
              key={activeTab}
              ref={processedZoomRef}
              src={processedImageSrc.src ?? selectedProcessedImage.url}
              alt={`生成外観 ${currentTab ? currentTab.index + 1 : 1}`}
              isDragging={
                excludeAreaSelection.isExcDragging ||
                (isSubAreaOverlayOnProcessed && subAreaSelection.isDragging)
              }
              maxScale={AI_COLOR_MAX_ZOOM_SCALE}
              minScale={canvasMinZoomScale}
              panDisabled={
                ((isSubAreaOverlayOnProcessed && permissions.canEditCustomArea) ||
                  isExcludeEditing) &&
                !isCanvasPanMode
              }
              grabCursor={
                ((isSubAreaOverlayOnProcessed && permissions.canEditCustomArea) ||
                  isExcludeEditing) &&
                isCanvasPanMode
              }
              onScaleChange={setProcessedZoomScale}
              // プレビュー表示中の naturalWidth/Height は原寸ではないので originalDimensions に書かない。
              // 原寸を読み込み終えたことは hook にも知らせ、編集→閲覧の切替でプレビューに戻さない
              onLoad={
                processedImageSrc.isFullRes
                  ? e => {
                      handleImageLoad(e)
                      processedImageSrc.onFullResLoad()
                    }
                  : undefined
              }
            >
              {/* ベースエリアフォーカス時のオーバーレイ */}
              {originalDimensions && isEditMode && !showOriginalImage && focusedBaseArea && (
                <MaskColorOverlay
                  maskUrls={currentMaskUrls}
                  focusedArea={focusedBaseArea}
                  getMetrics={getSubAreaMetrics}
                  // 雨樋・破風フォーカス時はカスタムエリアを切り抜かない（最高優先のため）
                  excludePolygons={
                    focusedBaseArea === '雨樋' || focusedBaseArea === '破風・鼻隠し'
                      ? undefined
                      : allExcludePolygons
                  }
                  // 当該ベースエリアに統合されたカスタムエリア領域を union 表示（issue #2224）
                  includePolygons={mergedPolygonsByArea[focusedBaseArea]}
                  originalDimensions={originalDimensions}
                />
              )}
              {/* 企業側: カスタムエリアフォーカス時のSVGオーバーレイ（編集可能） */}
              {originalDimensions &&
                isSubAreaOverlayOnProcessed &&
                permissions.canEditCustomArea && (
                  <div onPointerDownCapture={() => setLastEditContext('subarea')}>
                    <AreaOverlay
                      areas={subAreaSelection.subAreas}
                      draggingPointIndex={subAreaSelection.draggingPointIndex}
                      draggingPolygonIndex={subAreaSelection.draggingPolygonIndex}
                      getMetrics={getSubAreaMetrics}
                      interactionDisabled={isCanvasPanMode}
                      isDragging={subAreaSelection.isDragging}
                      onDeletePoint={subAreaSelection.handleDeletePoint}
                      onDragEnd={subAreaSelection.handleDragEnd}
                      onDragStart={subAreaSelection.handleDragStart}
                      onInsertPoint={info =>
                        subAreaSelection.handleInsertPoint(
                          info.edgeIndex,
                          info.originalX,
                          info.originalY,
                          info.polygonIndex,
                        )
                      }
                      onOverlayClick={subAreaSelection.handleOverlayClick}
                      onOverlayMove={subAreaSelection.handleOverlayMove}
                      onVertexContextMenu={handleSubAreaVertexContextMenu}
                      originalDimensions={originalDimensions}
                      selectedAreaIndex={subAreaSelection.selectedSubAreaIndex}
                      selectedVertex={subAreaSelection.selectedVertex}
                      zoomScale={processedZoomScale}
                    />
                  </div>
                )}
              {/* 施主側: カスタムエリアフォーカス時のパターンオーバーレイ（表示のみ） */}
              {originalDimensions &&
                isEditMode &&
                !showOriginalImage &&
                isCustomerSubAreaFocused &&
                focusedSubAreaData &&
                focusedSubAreaData.polygons.length > 0 && (
                  <PolygonMaskOverlay
                    polygons={focusedSubAreaData.polygons}
                    originalDimensions={originalDimensions}
                    getMetrics={getSubAreaMetrics}
                    useDetectedAreaOnly={focusedSubAreaData.useDetectedAreaOnly}
                    baseMaskUrls={maskUrlsForCustomArea}
                    // 雨樋・破風色指定がある場合のみ除外。未指定ならカスタム色で塗り潰し可能
                    // （真理表に準拠。original パネル・Python 側と同条件）
                    gutterMaskUrl={areaColors['雨樋'] ? currentMaskUrls['雨樋'] : undefined}
                    fasciaMaskUrl={
                      areaColors['破風・鼻隠し'] ? currentMaskUrls['破風・鼻隠し'] : undefined
                    }
                    // ベースエリアに統合されたポリゴン（issue #2224）。Python の augmented マスクと演算を揃える
                    // （雨樋・破風の統合分は真理表に従い当該色指定時のみ差し引きに含める）
                    baseMergedPolygons={baseMergedPolygonsForCustomArea}
                    gutterMergedPolygons={
                      areaColors['雨樋'] ? mergedPolygonsByArea['雨樋'] : undefined
                    }
                    fasciaMergedPolygons={
                      areaColors['破風・鼻隠し'] ? mergedPolygonsByArea['破風・鼻隠し'] : undefined
                    }
                  />
                )}
              {originalDimensions && isEditMode && excludeAreaSelection.excludeEnabled && (
                <div onPointerDownCapture={() => setLastEditContext('exclude')}>
                  <AreaOverlay
                    areas={excludeAreaSelection.excludeAreas}
                    draggingPointIndex={excludeAreaSelection.draggingExcPointIndex}
                    getMetrics={getProcessedMetrics}
                    interactionDisabled={isCanvasPanMode}
                    isDragging={excludeAreaSelection.isExcDragging}
                    onDeletePoint={excludeAreaSelection.handleDeleteExcPoint}
                    onDragEnd={excludeAreaSelection.handleExcDragEnd}
                    onDragStart={excludeAreaSelection.handleExcDragStart}
                    onInsertPoint={info =>
                      excludeAreaSelection.handleInsertExcPoint(
                        info.edgeIndex,
                        info.originalX,
                        info.originalY,
                      )
                    }
                    onOverlayClick={excludeAreaSelection.handleResultOverlayClick}
                    onOverlayMove={e => {
                      excludeAreaSelection.handleResultOverlayMove(
                        e,
                        (e.currentTarget as HTMLElement).getBoundingClientRect(),
                      )
                    }}
                    onVertexContextMenu={handleExcludeVertexContextMenu}
                    originalDimensions={originalDimensions}
                    overlayTestId="result-overlay"
                    pointTestId="exclude-point"
                    selectedAreaIndex={excludeAreaSelection.selectedExcIdx}
                    selectedVertex={
                      excludeAreaSelection.selectedVertexIndex !== null
                        ? {
                            pointIndex: excludeAreaSelection.selectedVertexIndex,
                            polygonIndex: -1,
                          }
                        : null
                    }
                    selectedColor="orange"
                    selectedFillColor="rgba(255,165,0,0.15)"
                    unselectedColor="rgba(0,0,0,0.4)"
                    unselectedFillColor="rgba(0,0,0,0.15)"
                    zoomScale={processedZoomScale}
                  />
                </div>
              )}
            </ZoomableImage>
          ),
          badge: isPreviewMode ? (
            <span className={`absolute inline-flex items-center left-4 top-4 ${previewLabelClass}`}>
              生成画像
            </span>
          ) : (
            <span className="absolute inline-flex items-center px-4 py-2 text-xs rounded-full shadow left-4 top-4 gap-2 border-lime-200 bg-gradient-to-br from-[#86FF80] to-[#FFF370]">
              <Sparkles aria-hidden className="w-3 h-3" />
              {formatViewerDate(
                selectedProcessedImage.uploadedAt
                  ? timestampDate(selectedProcessedImage.uploadedAt)
                  : undefined,
              )}
            </span>
          ),
          toolbar:
            (isSubAreaOverlayOnProcessed && permissions.canEditCustomArea && originalDimensions) ||
            isExcludeEditing
              ? renderCanvasToolbar(
                  // 両方のオーバーレイが同一パネルに乗る場合は最後に操作した方に undo を届ける
                  isExcludeEditing && isSubAreaOverlayOnProcessed && permissions.canEditCustomArea
                    ? lastEditContext
                    : isExcludeEditing
                      ? 'exclude'
                      : 'subarea',
                )
              : undefined,
          zoomControl: (
            <ZoomControls
              maxScale={AI_COLOR_MAX_ZOOM_SCALE}
              minScale={canvasMinZoomScale}
              onReset={() => processedZoomRef.current?.resetTransform()}
              onZoomIn={() => processedZoomRef.current?.zoomIn(AI_COLOR_ZOOM_STEP)}
              onZoomOut={
                canZoomOutCanvas
                  ? () => processedZoomRef.current?.zoomOut(AI_COLOR_ZOOM_STEP)
                  : undefined
              }
              scale={processedZoomScale}
            />
          ),
        }
      : undefined

  // 共有リンクのアンカー（施主）。帯は選択中のときだけ、ピルは別の履歴を見ているときだけ出す
  const anchorThumbnail = findThumbnailByImageId(sharedAnchor?.imageId)
  // 帯と戻りピルに出すのはタイトルだけ。未設定の既定表示（年月日と担当者名）は出さない
  const anchorResolvedTitle = anchorThumbnail ? resolveHistoryTitle(anchorThumbnail) : undefined
  const anchorTitle =
    anchorResolvedTitle && !anchorResolvedTitle.isDefault ? anchorResolvedTitle.text : undefined
  // 共有ダイアログに出すアンカーの履歴（ピンの付け外しで最新の状態に追従させるため毎回引く）
  const shareAnchorItem = findThumbnailByImageId(shareAnchorImageId ?? undefined)
  // 共有ダイアログの中でタイトルを付ける（藤井の決定、2026-10-28）。履歴カードと同じ保存経路
  const shareAnchorTitleTargetId = shareAnchorItem?.imageId
  const handleSaveShareAnchorTitle =
    shareAnchorTitleTargetId !== undefined && permissions.canEditHistoryTitle && onUpdateImageTitle
      ? (title: string) => handleSaveTitle(shareAnchorTitleTargetId, title)
      : undefined
  const shareAnchorCutLabel = shareAnchorItem
    ? `カット${thumbnailGroups.findIndex(group => group.tabValue === shareAnchorItem.tabValue) + 1}`
    : ''
  const selectedHistoryTitle = selectedProcessedImage
    ? findThumbnailByImageId(selectedProcessedImage.id)
    : undefined

  return (
    <div className="flex flex-col gap-6">
      {sharedAnchor && anchorThumbnail && anchorThumbnail.isActive ? (
        <SharedAnchorBanner companyName={sharedAnchor.companyName} title={anchorTitle} />
      ) : null}
      {sharedAnchor && anchorThumbnail && !anchorThumbnail.isActive ? (
        <SharedAnchorReturnPill
          onReturn={() => handleThumbnailSelect(anchorThumbnail)}
          title={anchorTitle}
        />
      ) : null}
      <SimulationDetailHeader
        data={data}
        dealNameMutation={permissions.canEditDealName ? dealNameMutation : undefined}
        surveyReportUrl={surveyReportUrl}
        onSurveyReportClick={onSurveyReportClick}
        selectedCreatorName={selectedProcessedImage?.createdByName}
      />

      <SimulationEditorCard
        isEditMode={isEditMode}
        header={
          <SimulationControlBar
            title={controlBarTitle}
            isPreviewMode={isPreviewMode}
            isOriginalHidden={isOriginalHidden}
            onOriginalHiddenChange={setIsOriginalHidden}
            isVerticalLayout={isVerticalLayout}
            onVerticalLayoutChange={setIsVerticalLayout}
            canDownload={Boolean(selectedProcessedImage?.url)}
            isDownloading={isDownloading}
            onDownload={handleDownload}
            canDownloadSummary={Boolean(
              selectedProcessedImage?.url && selectedProcessedImage?.parameters,
            )}
            isSummaryDownloading={isSummaryDownloading}
            onDownloadSummary={handleDownloadSummary}
            canShare={permissions.canShare && Boolean(createShareLink)}
            showShareButton={permissions.canShare}
            onShare={
              permissions.canShare && createShareLink
                ? () => handleShareDialogOpenChange(true)
                : undefined
            }
            onToggleMode={() => setDisplayMode(isPreviewMode ? 'edit' : 'preview')}
            idPrefix={`simulation-${activeTab}`}
          />
        }
        image={
          tabs.length > 0 && currentTab && currentImage ? (
            <div className="flex flex-col gap-2">
              <SimulationImagePanel
                layout={isVerticalLayout ? 'vertical' : 'horizontal'}
                onBackgroundClick={handleDismissColorPalette}
                original={originalSlot}
                processed={processedSlot}
              />
              {/* 生成結果の期待値調整（issue #2417）。生成外観が出ているときだけ添える */}
              {selectedProcessedImage ? (
                <p className="text-xs text-muted-foreground">{AI_COLOR_RESULT_DISCLAIMER}</p>
              ) : null}
            </div>
          ) : null
        }
        sidePanel={
          isEditMode && tabs.length > 0 && currentTab && currentImage ? (
            <>
              {maskAdjustError ? (
                <p className="text-xs text-red-600 max-w-[theme(width.72)] break-words">
                  {maskAdjustError}
                </p>
              ) : null}
              <div className="relative flex flex-col gap-2 lg:min-h-[638px] lg:flex-1 lg:self-stretch lg:overflow-visible">
                <AreaColorSelector
                  areaColors={areaColors}
                  areaMaterials={areaMaterials}
                  areaSelectionMode={areaSelectionMode}
                  baseAreas={visibleBaseAreas ?? BASE_AREAS}
                  canEditMaterial={permissions.canEditMaterial}
                  canProcessImage={canProcessImage}
                  colorOptions={displayColorOptions}
                  materialOptions={availableMaterials}
                  isExcludeAreaModeActive={
                    permissions.canEditExcludeArea && excludeAreaSelection.excludeEnabled
                  }
                  isMaskRegenerating={isMaskRegenerating}
                  isSubAreaModeActive={isSubAreaModeActive}
                  maskRegenerationLimitedAreas={currentImageMaskLimitedAreas}
                  maskPollingPhase={maskPollingPhase}
                  openLayerId={openLayerId}
                  permissions={permissions}
                  processing={processing}
                  selectedExcludeIdx={excludeAreaSelection.selectedExcIdx}
                  selectedSubAreaIndex={subAreaSelection.selectedSubAreaIndex}
                  subAreas={subAreaSelection.subAreas}
                  unifyColor={unifyColor}
                  onBaseAreaAdjustClick={
                    permissions.canRegenerateMask ? handleBaseAreaAdjust : undefined
                  }
                  onBaseAreaColorClick={handleOpenColorPalette}
                  onBaseAreaResetClick={handleBaseAreaReset}
                  onCloseColorPaletteOnly={handleCloseColorPalettePanel}
                  onColorPaletteClose={handleDismissColorPalette}
                  onExcludeAreaAddClick={
                    permissions.canEditExcludeArea
                      ? () => {
                          // カスタムエリアモードを先にOFFにしてから除外エリアを追加
                          if (isSubAreaModeActive) {
                            setIsSubAreaModeActive(false)
                          }
                          if (!excludeAreaSelection.excludeEnabled) {
                            excludeAreaSelection.setExcludeEnabled(true)
                          }
                          excludeAreaSelection.handleAddExcludeArea()
                        }
                      : undefined
                  }
                  onExcludeAreaDeleteClick={
                    permissions.canEditExcludeArea
                      ? excludeAreaSelection.removeExcludeArea
                      : undefined
                  }
                  onExcludeAreaModeToggle={
                    permissions.canEditExcludeArea
                      ? () => {
                          const nextExcludeEnabled = !excludeAreaSelection.excludeEnabled
                          excludeAreaSelection.setExcludeEnabled(nextExcludeEnabled)
                          // モードをONにした際、除外エリアが空なら自動的に1つ追加
                          if (
                            nextExcludeEnabled &&
                            excludeAreaSelection.excludeAreas.length === 0
                          ) {
                            excludeAreaSelection.handleAddExcludeArea()
                          }
                        }
                      : undefined
                  }
                  onExcludeAreaSelectClick={
                    permissions.canEditExcludeArea
                      ? excludeAreaSelection.setSelectedExcIdx
                      : undefined
                  }
                  onOpenLayerChange={setOpenLayerId}
                  onProcessClick={handleProcessImage}
                  onSubAreaAddClick={
                    permissions.canEditCustomArea
                      ? () => {
                          // 除外エリアモードを先にOFFにしてからカスタムエリアを追加
                          if (excludeAreaSelection.excludeEnabled) {
                            excludeAreaSelection.setExcludeEnabled(false)
                          }
                          if (!isSubAreaModeActive) {
                            setIsSubAreaModeActive(true)
                          }
                          subAreaSelection.handleAddSubArea()
                        }
                      : undefined
                  }
                  onSubAreaColorClick={
                    // 施主側でも色指定は可能（ポリゴン編集のみ不可）
                    subAreaSelection.subAreas.length > 0 || permissions.canEditCustomArea
                      ? idx => {
                          subAreaSelection.setSelectedSubAreaIndex(idx)
                          handleOpenColorPalette(`subarea-${idx}`)
                        }
                      : undefined
                  }
                  onSubAreaResetClick={
                    // 施主側でも色指定解除は可能
                    subAreaSelection.subAreas.length > 0 || permissions.canEditCustomArea
                      ? idx => {
                          subAreaSelection.setSubAreaColor(idx, '')
                        }
                      : undefined
                  }
                  onSubAreaDeleteClick={
                    permissions.canEditCustomArea
                      ? idx => {
                          handleDismissColorPalette()
                          subAreaSelection.removeSubArea(idx)
                        }
                      : undefined
                  }
                  onSubAreaModeToggle={
                    // 施主側でもカスタムエリアモードの切り替えは可能（ただし追加はできない）
                    subAreaSelection.subAreas.length > 0 || permissions.canEditCustomArea
                      ? () => {
                          const nextModeState = !isSubAreaModeActive
                          setIsSubAreaModeActive(nextModeState)
                          // モードをONにした際、サブエリアが空なら自動的に1つ追加（企業側のみ）
                          if (
                            permissions.canEditCustomArea &&
                            nextModeState &&
                            subAreaSelection.subAreas.length === 0
                          ) {
                            subAreaSelection.handleAddSubArea()
                          }
                        }
                      : undefined
                  }
                  onSubAreaSelectClick={
                    // 施主側でもカスタムエリアの選択（フォーカス）は可能
                    subAreaSelection.subAreas.length > 0
                      ? subAreaSelection.setSelectedSubAreaIndex
                      : undefined
                  }
                  // issue #2413: 一覧から「ベースエリア内のみ有効」を直接 ON/OFF する機能は、
                  // 塗り方オプション全体の整理（#2541）にまとめるためリリースを見送り（2026-09-15 PdM 判断）。
                  // 再開時はこの配線を戻すだけでよい（AreaColorSelector は prop 未指定なら従来の 1 行表示）。
                  // onSubAreaUseDetectedAreaOnlyChange={
                  //   permissions.canConfigureCustomArea
                  //     ? subAreaSelection.setSubAreaUseDetectedAreaOnly
                  //     : undefined
                  // }
                  onSubAreaSettingClick={
                    permissions.canConfigureCustomArea
                      ? idx => {
                          handleCloseColorPalettePanel()
                          subAreaSelection.setSelectedSubAreaIndex(idx)
                          setEditingCustomAreaIndex(idx)
                          setIsCustomAreaSettingOpen(true)
                        }
                      : undefined
                  }
                  onUnifyColorChange={setUnifyColor}
                  excludeAreas={
                    permissions.canEditExcludeArea ? excludeAreaSelection.excludeAreas : []
                  }
                />
                {editingArea ? (
                  /*
                    key={editingArea} は必須（見た目上の意味は無い）.
                    ダイアログ内の「選択中タブ（activeTab）」と「色フィルタ（メーカー/トーン/検索）」は
                    編集中エリアのスコープに属する state だが、パネルを開いたまま別エリアのボタンを
                    押すと editingArea は truthy→truthy で遷移するため再マウントされず、前のエリアの
                    タブ・フィルタが残留する（素材タブのまま色一覧が出ない / 0色表示）。
                    key で remount させてスコープを構造的に表現しており、将来 ColorSelectDialog や
                    useColorFilter に state が増えても自動的にこの不変条件が守られる.

                    カット（activeTab）も key に含める. 現状はカット切替時に
                    useLayerEditingStateSync の effect が editingArea を null にするためダイアログは
                    閉じるが、それは離れた場所の副作用への依存であり脆い。施主のカスタムエリアは
                    色候補がカット依存（area_key = "<base_image_id>:<index>"）なので、その effect が
                    将来変わるとカット跨ぎでフィルタが持ち越される。key に含めておけば構造的に防げる.
                  */
                  <ColorSelectDialog
                    className="order-2 lg:absolute lg:inset-y-0 lg:right-full lg:z-10 lg:order-none"
                    colors={dialogColors}
                    key={`${activeTab}:${editingArea}`}
                    initialActiveTab={areaSelectionMode[editingArea]}
                    materials={availableMaterials ?? []}
                    materialTargetPart={
                      permissions.canEditMaterial ? materialTargetPart : undefined
                    }
                    onClose={handleCloseColorPalettePanel}
                    onColorSelect={handleColorSelect}
                    onMaterialSelect={handleMaterialSelect}
                    selectedColor={selectedColorInPalette}
                    selectedMaterialId={selectedMaterialIdInPalette}
                    showPresetSelector={
                      permissions.canSelectPreset && (colorPresets?.length ?? 0) > 0
                    }
                    presets={colorPresets ?? []}
                    selectedPresetId={effectivePresetId}
                    onPresetChange={handlePresetChange}
                  />
                ) : null}
              </div>
            </>
          ) : null
        }
      />

      {thumbnailGroups.length > 0 && role === 'customer' ? (
        <div className="flex flex-col gap-5">
          <CustomerPinnedStrip
            companyName={sharedAnchor?.companyName}
            items={pinnedItems}
            onSelect={handleThumbnailSelect}
          />
          <CustomerHistoryList
            filterNote={
              historyFilter === 'favorited'
                ? 'お気に入りにした履歴だけを表示しています'
                : historyFilter === 'pinned'
                  ? 'ピン留めされた履歴だけを表示しています'
                  : undefined
            }
            groups={filteredThumbnailGroups}
            headerAccessory={
              <HistoryFilterChips
                counts={historyCounts}
                onChange={setHistoryFilter}
                value={historyFilter}
              />
            }
            onEditTitle={
              permissions.canEditHistoryTitle && onUpdateImageTitle
                ? setTitleSheetTarget
                : undefined
            }
            onSelect={handleThumbnailSelect}
            onToggleFavorite={onToggleImageFavorite}
          />
          <CustomerTitleSheet
            item={titleSheetTarget}
            onClose={() => setTitleSheetTarget(null)}
            onSave={handleSaveTitle}
          />
        </div>
      ) : null}

      {thumbnailGroups.length > 0 && role !== 'customer' ? (
        <>
          {permissions.canPin ? (
            <PinnedHistoryPanel
              items={pinnedItems}
              onSaveTitle={onUpdateImageTitle ? handleSaveTitle : undefined}
              onSelect={({ item }) => handleThumbnailSelect(item)}
              onToggleFavorite={onToggleImageFavorite}
              onUnpin={imageId => {
                const item = findThumbnailByImageId(imageId)
                if (item) void handleTogglePin(item)
              }}
            />
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-foreground">履歴</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">絞り込み</span>
              <HistoryFilterChips
                counts={historyCounts}
                onChange={setHistoryFilter}
                pinLimit={AI_COLOR_PIN_LIMIT}
                showAllCount
                value={historyFilter}
              />
            </div>
          </div>
          {filteredThumbnailGroups.map(group => (
            <Card key={group.key} className="p-6 border border-slate-200 shadow-sm">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">{group.title}</h3>
                  <span className="text-xs font-medium text-slate-500">{group.date}</span>
                </div>
                {group.items.length === 0 ? (
                  <p className="text-sm text-slate-500">該当する履歴はありません。</p>
                ) : null}
                <div className="flex pb-1 overflow-x-auto gap-3">
                  {group.items.map(thumb => (
                    <AiImageCard
                      key={thumb.key}
                      categoryTag={thumb.categoryTag}
                      createdAt={thumb.createdAt ? formatTimestamp(thumb.createdAt) : undefined}
                      createdBy={thumb.createdBy}
                      disableDelete={!permissions.canDeleteImage || !thumb.canDelete}
                      idPrefix={thumb.key}
                      isPinned={Boolean(thumb.pinnedAt)}
                      isSelected={thumb.isActive}
                      isLiked={thumb.favorited}
                      label={thumb.label}
                      onDelete={
                        permissions.canDeleteImage &&
                        thumb.canDelete &&
                        thumb.imageId &&
                        onDeleteSimulatedImage
                          ? () => onDeleteSimulatedImage(thumb.imageId!)
                          : undefined
                      }
                      onSaveTitle={
                        thumb.type === 'processed' &&
                        thumb.imageId &&
                        permissions.canEditHistoryTitle &&
                        onUpdateImageTitle
                          ? title => handleSaveTitle(thumb.imageId!, title)
                          : undefined
                      }
                      onSelect={thumb.tabValue ? () => handleThumbnailSelect(thumb) : undefined}
                      onToggleLike={
                        thumb.type === 'processed' && thumb.imageId && onToggleImageFavorite
                          ? () => onToggleImageFavorite(thumb.imageId!, !thumb.favorited)
                          : undefined
                      }
                      onTogglePin={
                        thumb.type === 'processed' &&
                        thumb.imageId &&
                        permissions.canPin &&
                        onUpdateImagePin
                          ? () => void handleTogglePin(thumb)
                          : undefined
                      }
                      pinLimitReached={!thumb.pinnedAt && pinnedItems.length >= AI_COLOR_PIN_LIMIT}
                      resolvedTitle={
                        thumb.type === 'processed'
                          ? resolveHistoryTitle(thumb)
                          : { text: '現況写真', isDefault: false }
                      }
                      title={thumb.title}
                      url={thumb.url}
                    />
                  ))}
                </div>
              </div>
            </Card>
          ))}
          <PinReplaceDialog
            candidateTitle={pinReplaceTarget ? resolveHistoryTitle(pinReplaceTarget).text : ''}
            onOpenChange={open => {
              if (!open) setPinReplaceTarget(null)
            }}
            onReplace={async replaceImageId => {
              if (!pinReplaceTarget?.imageId || !onUpdateImagePin) return
              try {
                await onUpdateImagePin(pinReplaceTarget.imageId, true, replaceImageId)
              } catch (error) {
                handleError(error)
                throw error
              }
            }}
            open={pinReplaceTarget !== null}
            pinnedItems={pinnedItems.map(({ item, cutLabel }) => ({
              imageId: item.imageId ?? 0,
              url: item.url,
              title: resolveHistoryTitle(item),
              cutLabel,
              createdBy: item.createdBy,
              createdAt: item.createdAt ? formatTimestamp(item.createdAt) : undefined,
            }))}
          />
        </>
      ) : null}

      {thumbnailGroups.length === 0 ? (
        <Card className="p-6 border border-slate-200 shadow-sm">
          <p className="text-sm text-slate-500">アップロード済みの外観カットはありません。</p>
        </Card>
      ) : null}

      {permissions.canUploadImage && s3Upload
        ? (() => {
            const uploadCard = (
              <SimulationUploadCard
                ariaLabel="新しい外観カットをアップロード"
                inputId="simulation-detail-upload-input"
                s3Upload={s3Upload}
                title="新しい外観カットを作成"
                uploadErrorNote={uploadErrorNote}
                uploading={uploading}
              />
            )
            return renderUploadCard ? renderUploadCard(uploadCard) : uploadCard
          })()
        : null}

      {isEditMode && permissions.canConfigureCustomArea ? (
        <CustomAreaSettingDialog
          onOpenChange={handleCustomAreaSettingOpenChange}
          onSave={handleCustomAreaSettingSave}
          open={isCustomAreaSettingOpen}
          subAreas={subAreaSelection.subAreas}
          targetIndex={editingCustomAreaIndex}
        />
      ) : null}
      {vertexMenu && (
        <VertexContextMenu
          onClose={() => setVertexMenu(null)}
          onDelete={() => {
            if (vertexMenu.context === 'exclude') {
              excludeAreaSelection.handleDeleteExcPoint(vertexMenu.pointIndex)
            } else {
              subAreaSelection.handleDeletePoint(vertexMenu.pointIndex, vertexMenu.polygonIndex)
            }
          }}
          position={{ x: vertexMenu.x, y: vertexMenu.y }}
        />
      )}
      <ShareDialog
        errorMessage={shareError}
        expiresAt={shareExpiresAt}
        fallbackReason={shareFallbackReason}
        isLoading={isShareLinkLoading}
        onCloseAndExecute={() => setIsShareDialogOpen(false)}
        onOpenChange={handleShareDialogOpenChange}
        onPickAnotherHistory={() => setIsShareDialogOpen(false)}
        onSaveTitle={handleSaveShareAnchorTitle}
        open={isShareDialogOpen}
        pinState={
          shareAnchorItem && permissions.canPin && onUpdateImagePin
            ? {
                count: pinnedItems.length,
                isPinned: Boolean(shareAnchorItem.pinnedAt),
                pending: sharePinPending,
                onToggle: async pinned => {
                  if (!shareAnchorItem.imageId) return
                  setSharePinPending(true)
                  try {
                    await onUpdateImagePin(shareAnchorItem.imageId, pinned)
                  } catch (error) {
                    handleError(error)
                  } finally {
                    setSharePinPending(false)
                  }
                },
              }
            : undefined
        }
        projectName={data.dealName ?? 'プロジェクト'}
        selectedHistoryTitle={
          selectedHistoryTitle ? resolveHistoryTitle(selectedHistoryTitle).text : undefined
        }
        sharedHistory={
          shareAnchorItem
            ? {
                url: shareAnchorItem.url,
                title: resolveHistoryTitle(shareAnchorItem),
                savedTitle: shareAnchorItem.title ?? '',
                cutLabel: shareAnchorCutLabel,
                createdBy: shareAnchorItem.createdBy,
                createdAt: shareAnchorItem.createdAt
                  ? formatTimestamp(shareAnchorItem.createdAt)
                  : undefined,
                isPinned: Boolean(shareAnchorItem.pinnedAt),
                favorited: Boolean(shareAnchorItem.favorited),
              }
            : undefined
        }
        shareUrl={shareUrl}
      />
    </div>
  )
}
